import { useState } from 'react';
import { Trash2, Edit, MapPin, Scale, MoreVertical } from 'lucide-react';
import type { SpoolWithRelations, CreateSpoolInput, MoveSpoolInput } from '@maker-toolchest/shared';
import { Select } from '@/components/Select';
import { Pagination } from '@/components/Pagination';
import { Spinner } from '@/components/Spinner';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DropdownMenu } from '@/components/DropdownMenu';
import { ColorSwatches } from '@/features/filaments';
import {
  useSpools,
  useDeleteSpool,
  useMoveSpool,
  useUpdateSpoolWeight,
  useCreateSpool,
  useUpdateSpool,
} from '../hooks';
import type { SpoolQueryParams } from '../api';
import { SpoolStatusBadge } from './SpoolStatusBadge';
import { SpoolForm } from './SpoolForm';
import { MoveSpoolDialog } from './MoveSpoolDialog';
import { WeightAdjustDialog } from './WeightAdjustDialog';

// Move constants outside component to prevent recreation on every render
const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'IN_STORAGE', label: 'In Storage' },
  { value: 'LOADED', label: 'Loaded' },
  { value: 'IN_USE', label: 'In Use' },
  { value: 'EMPTY', label: 'Empty' },
  { value: 'DISCARDED', label: 'Discarded' },
];

const LOCATION_TYPE_OPTIONS = [
  { value: '', label: 'All Locations' },
  { value: 'PRINTER', label: 'On Printer' },
  { value: 'STORAGE', label: 'In Storage' },
  { value: 'NONE', label: 'No Location' },
];

const SORT_BY_OPTIONS = [
  { value: 'createdAt', label: 'Date Added' },
  { value: 'remainingWeight', label: 'Weight' },
  { value: 'purchaseDate', label: 'Purchase Date' },
  { value: 'status', label: 'Status' },
];

interface SpoolTableProps {
  initialFilters?: Partial<SpoolQueryParams>;
}

export function SpoolTable({ initialFilters }: SpoolTableProps) {
  const [filters, setFilters] = useState<SpoolQueryParams>({
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    ...initialFilters,
  });
  const [formOpen, setFormOpen] = useState(false);
  const [editingSpool, setEditingSpool] = useState<SpoolWithRelations | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SpoolWithRelations | null>(null);
  const [moveTarget, setMoveTarget] = useState<SpoolWithRelations | null>(null);
  const [weightTarget, setWeightTarget] = useState<SpoolWithRelations | null>(null);

  const { data, isLoading } = useSpools(filters);
  const deleteMutation = useDeleteSpool();
  const moveMutation = useMoveSpool();
  const weightMutation = useUpdateSpoolWeight();
  const createMutation = useCreateSpool();
  const updateMutation = useUpdateSpool();

  const spools = data?.data || [];
  const pagination = data?.meta?.pagination;

  const handleFilterChange = (key: keyof SpoolQueryParams, value: string | undefined) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const handleEdit = (spool: SpoolWithRelations) => {
    setEditingSpool(spool);
    setFormOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch {
      // Error handled by mutation
    }
  };

  const handleMove = async (data: MoveSpoolInput) => {
    if (!moveTarget) return;
    try {
      await moveMutation.mutateAsync({ id: moveTarget.id, data });
      setMoveTarget(null);
    } catch {
      // Error handled by mutation
    }
  };

  const handleWeightUpdate = async (remainingWeight: number) => {
    if (!weightTarget) return;
    try {
      await weightMutation.mutateAsync({ id: weightTarget.id, remainingWeight });
      setWeightTarget(null);
    } catch {
      // Error handled by mutation
    }
  };

  const handleFormSubmit = async (formData: CreateSpoolInput) => {
    try {
      if (editingSpool) {
        await updateMutation.mutateAsync({ id: editingSpool.id, data: formData });
      } else {
        await createMutation.mutateAsync(formData);
      }
      setFormOpen(false);
      setEditingSpool(null);
    } catch {
      // Error handled by mutation
    }
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditingSpool(null);
  };

  const getLocationDisplay = (spool: SpoolWithRelations): string => {
    if (spool.locationType === 'PRINTER' && spool.tenantPrinter) {
      return spool.tenantPrinter.instanceName || spool.tenantPrinter.printer?.name || '-';
    }
    if (spool.locationType === 'STORAGE' && spool.storageLocation) {
      return spool.storageLocation.name;
    }
    return '-';
  };

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-40">
          <Select
            label="Status"
            value={filters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            options={STATUS_OPTIONS}
          />
        </div>
        <div className="w-40">
          <Select
            label="Location"
            value={filters.locationType || ''}
            onChange={(e) => handleFilterChange('locationType', e.target.value)}
            options={LOCATION_TYPE_OPTIONS}
          />
        </div>
        <div className="w-36">
          <Select
            label="Sort By"
            value={filters.sortBy || 'createdAt'}
            onChange={(e) => handleFilterChange('sortBy', e.target.value as SpoolQueryParams['sortBy'])}
            options={SORT_BY_OPTIONS}
          />
        </div>
      </div>

      {/* Table */}
      {spools.length === 0 ? (
        <div className="rounded-lg border border-border bg-background p-8 text-center">
          <p className="text-foreground-muted">
            {filters.status || filters.locationType
              ? 'No spools found matching your filters.'
              : "You haven't added any spools yet."}
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-background-subtle">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-foreground-muted">
                    Filament
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-foreground-muted">
                    Weight
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-foreground-muted">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-foreground-muted">
                    Location
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-foreground-muted">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-background">
                {spools.map((spool) => {
                  const filament = spool.tenantFilament?.filament;
                  const percentRemaining = spool.initialWeight > 0
                    ? Math.round((spool.remainingWeight / spool.initialWeight) * 100)
                    : 0;

                  return (
                    <tr key={spool.id} className="hover:bg-background-subtle/50">
                      <td className="whitespace-nowrap px-4 py-3">
                        <div className="flex items-center gap-3">
                          <ColorSwatches
                            colors={filament?.colorHexes || []}
                            size="md"
                          />
                          <div>
                            <div className="text-sm font-medium text-foreground">
                              {filament?.name || 'Unknown'}
                            </div>
                            <div className="text-xs text-foreground-muted">
                              {filament?.manufacturer?.name} - {filament?.colorName}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-24">
                            <div className="h-1.5 overflow-hidden rounded-full bg-background-subtle">
                              <div
                                className="h-full rounded-full bg-primary transition-all"
                                style={{ width: `${percentRemaining}%` }}
                              />
                            </div>
                          </div>
                          <span className="text-sm text-foreground-muted">
                            {spool.remainingWeight}g / {spool.initialWeight}g
                          </span>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <SpoolStatusBadge status={spool.status} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-foreground-muted">
                        {getLocationDisplay(spool)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <div className="flex items-center justify-end">
                          <DropdownMenu
                            trigger={
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Actions"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            }
                            items={[
                              {
                                label: 'Adjust Weight',
                                icon: <Scale className="h-4 w-4" />,
                                onClick: () => setWeightTarget(spool),
                              },
                              {
                                label: 'Move Spool',
                                icon: <MapPin className="h-4 w-4" />,
                                onClick: () => setMoveTarget(spool),
                              },
                              {
                                label: 'Edit',
                                icon: <Edit className="h-4 w-4" />,
                                onClick: () => handleEdit(spool),
                              },
                              {
                                label: 'Delete',
                                icon: <Trash2 className="h-4 w-4" />,
                                onClick: () => setDeleteTarget(spool),
                                variant: 'danger' as const,
                              },
                            ]}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pagination && pagination.totalPages > 1 && (
            <Pagination
              page={pagination.page}
              total={pagination.total}
              limit={pagination.limit}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}

      {/* Dialogs */}
      <SpoolForm
        open={formOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        spool={editingSpool}
        loading={createMutation.isPending || updateMutation.isPending}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Spool"
        description="Are you sure you want to delete this spool? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
      />

      <MoveSpoolDialog
        open={!!moveTarget}
        onClose={() => setMoveTarget(null)}
        onConfirm={handleMove}
        spool={moveTarget}
        loading={moveMutation.isPending}
      />

      <WeightAdjustDialog
        open={!!weightTarget}
        onClose={() => setWeightTarget(null)}
        onConfirm={handleWeightUpdate}
        spool={weightTarget}
        loading={weightMutation.isPending}
      />
    </div>
  );
}
